import { __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useState, useEffect } from 'react';
import { TabNav, Box, Octicon } from '@primer/react';
import { XIcon, HomeIcon, BrowserIcon } from '../../node_modules/@primer/octicons-react/dist/index.esm.js';
import { createUniqueID, padWithZero, isEnterOrSpacePressed } from '../../utils/functions.js';
import LTText from '../LTText/index.js';
import { StyledTabNav, StyledTabNavLink, StyledBox, StyledBoxHover, StyledTextBox, StyledIcon, StyledIconButton, CountSpan } from './styles.js';

/**
 * LTTabNav component.
 * @param {string} ariaLabel ARIA label to be added.
 * @param {string} activeTab used for navigation purpose.
 * @param {string} className CSS class for the outer element.
 * @param {!Object<string, *>} extraStyles - Styles for the TabNav.
 * @param {bool} isDashboardTabNavigation Whether the tab navigation is for the dashboard. Defaults to false
 * @param {bool} isLeadingSpaceRequired Whether the leading space is required.
 * @param {!Array<!Object<string, *>} items - options in the menu.
    Each key will have the following
   - name: string
   - id: string
   - count: number
   - isActive: boolean
 * @param {Function} onSelect - Handles value selection.
 * @param {Function} onDeselect - Deselects the value.
 * @returns {JSX.Element} LTTabNav component.
 */
var LTTabNav = function LTTabNav(_a) {
  var _b = _a.ariaLabel,
    ariaLabel = _b === void 0 ? "Main" : _b,
    _c = _a.activeTab,
    activeTab = _c === void 0 ? "" : _c,
    _d = _a.className,
    className = _d === void 0 ? "" : _d,
    _e = _a.extraStyles,
    extraStyles = _e === void 0 ? {} : _e,
    _f = _a.isDashboardTabNavigation,
    isDashboardTabNavigation = _f === void 0 ? false : _f,
    _g = _a.isLeadingSpaceRequired,
    isLeadingSpaceRequired = _g === void 0 ? false : _g,
    _h = _a.items,
    items = _h === void 0 ? [] : _h,
    _j = _a.onDeselect,
    onDeselect = _j === void 0 ? function () {} : _j,
    _k = _a.onSelect,
    onSelect = _k === void 0 ? function () {} : _k;
  var regainFocusHandler = function regainFocusHandler(activeTab) {
    var _a;
    var elementId = (_a = items === null || items === void 0 ? void 0 : items.find(function (item) {
      return (item === null || item === void 0 ? void 0 : item.id) === activeTab;
    })) === null || _a === void 0 ? void 0 : _a.id;
    var element = document.getElementById(elementId);
    if (element) {
      element === null || element === void 0 ? void 0 : element.focus();
    }
  };
  var handleSelectAction = function handleSelectAction(isActive, id, onSelect, isDisabled) {
    return function (e) {
      if (isActive || isDisabled) {
        return;
      }
      if (e.type === "click" || isEnterOrSpacePressed(e)) {
        e.stopPropagation();
        onSelect(id);
      }
    };
  };
  var handleDeselectAction = function handleDeselectAction(id, onDeselect) {
    return function (e) {
      if (e.type === "click" || isEnterOrSpacePressed(e)) {
        e.stopPropagation();
        onDeselect(id);
      }
    };
  };
  var uniqueId = useState(createUniqueID("tabnav_item_"))[0];
  useEffect(function () {
    var tabnavElement = document.getElementById(uniqueId);
    if (tabnavElement) {
      var tablistElement = tabnavElement.querySelector('[role="tablist"]');
      if (tablistElement) {
        tablistElement.setAttribute("aria-orientation", "horizontal");
      }
    }
  }, []);
  return /*#__PURE__*/React.createElement(StyledTabNav, {
    "aria-label": ariaLabel,
    className: "".concat(className, " tabNav"),
    id: uniqueId
  }, isLeadingSpaceRequired && /*#__PURE__*/React.createElement(TabNav.Link, {
    "aria-label": "Status Navigation",
    "aria-hidden": true,
    onFocus: function onFocus() {
      return regainFocusHandler(activeTab);
    },
    tabIndex: "-1"
  }), items === null || items === void 0 ? void 0 : items.map(function (tab, index) {
    var _a = tab.contentId,
      contentId = _a === void 0 ? undefined : _a,
      count = tab.count,
      id = tab.id,
      isActive = tab.isActive,
      _b = tab.isDisabled,
      isDisabled = _b === void 0 ? false : _b,
      _c = tab.name,
      name = _c === void 0 ? "" : _c,
      trailingDom = tab.trailingDom;
    var iconName = index === 0 ? HomeIcon : BrowserIcon;
    return /*#__PURE__*/React.createElement(StyledTabNavLink, {
      "aria-selected": isActive,
      "aria-controls": isActive ? contentId : undefined,
      as: isActive ? "div" : "a",
      id: id,
      isDashboardTabNavigation: isDashboardTabNavigation,
      key: "".concat(id, "-").concat(index),
      onClick: handleSelectAction(isActive, id, onSelect, isDisabled),
      onKeyDown: handleSelectAction(isActive, id, onSelect, isDisabled),
      selected: isActive,
      tabIndex: isActive ? 0 : -1,
      sx: __assign(__assign({}, extraStyles), {
        pointerEvents: isDisabled ? "none" : "all"
      })
    }, isDashboardTabNavigation && /*#__PURE__*/React.createElement(StyledBox, {
      sx: StyledBoxHover,
      as: "div",
      selected: isActive
    }, /*#__PURE__*/React.createElement(Box, {
      as: "div"
    }, /*#__PURE__*/React.createElement(Octicon, {
      "aria-label": "".concat(index === 0 ? "Home Icon" : "Browser Icon"),
      icon: iconName,
      size: 16
    })), /*#__PURE__*/React.createElement(StyledTextBox, {
      as: "div",
      isActive: isActive
    }, /*#__PURE__*/React.createElement(LTText, {
      as: "p",
      variant: LTText.TextVariant.SUBHEADER_REGULAR,
      text: name
    })), index > 0 && isActive && /*#__PURE__*/React.createElement(StyledIcon, {
      as: "div",
      onClick: handleDeselectAction(id, onDeselect),
      onKeyDown: handleDeselectAction(id, onDeselect),
      role: "button",
      tabIndex: isActive && index !== 0 ? 0 : -1,
      "aria-label": "Cancel Button"
    }, /*#__PURE__*/React.createElement(StyledIconButton, {
      as: "div"
    }, /*#__PURE__*/React.createElement(Octicon, {
      icon: XIcon,
      size: 15,
      "aria-label": "Cancel Icon",
      color: "#666666 !important"
    })))), !isDashboardTabNavigation && /*#__PURE__*/React.createElement(StyledBox, {
      sx: StyledBoxHover,
      as: "div",
      selected: isActive
    }, /*#__PURE__*/React.createElement("span", null, name), trailingDom && trailingDom, count >= 0 && /*#__PURE__*/React.createElement(CountSpan, {
      id: "count-".concat(id)
    }, padWithZero(count))));
  }));
};

export { LTTabNav as default };
